<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useFilters, useOrder, useOrderPayments } from "@/composables";
import { useValidation } from "@/composables/useValidation";
import {
  MandatoryPaymentTypes,
  TravelOrderPaymentStatus as TravelOrderPaymentStatusEnum,
  TravelOrderPaymentType,
  TravelOrderPaymentTypeLabel,
} from "@/constants/travelOrder";
import { TRAVEL_ORDER_STATUSES_WITHOUT_ACTIVE_PAYMENTS, TravelOrderStatus } from "@/constants/trip";
import { getEntityKey, isSameEntity } from "@/utils/sameEntity";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import TravelOrderPaymentStatus from "@/components/statuses/TravelOrderPaymentStatus.vue";
import TooltipButton from "@/components/buttons/TooltipButton.vue";

import type { TravelOrderPayment } from "@/api/trips/dto/trip.dto";
import type { DisableRule } from "@/types/validation";

const { t } = useI18n();
const { requiredValidator } = useValidation();
const { generateId } = useOrder();
const { price, round } = useFilters();
const { getPositiveRateRules } = useOrderPayments();

const payments = defineModel<TravelOrderPayment[]>({ default: () => [] });

const props = defineProps<{
  travelOrderStatus: TravelOrderStatus;
}>();

const emits = defineEmits<{
  (e: "scrollBottom"): void;
}>();

const isNoActivePayments = computed<boolean>(() => {
  return TRAVEL_ORDER_STATUSES_WITHOUT_ACTIVE_PAYMENTS.includes(props.travelOrderStatus);
});

const addPaymentDisableRules = computed<DisableRule[]>(() => [
  {
    disabled: isNoActivePayments.value,
    reasons: [t("payments.add-travel-order-payment-unavailable")],
  },
]);

const typeOptions = computed(() => {
  return Object.entries(TravelOrderPaymentTypeLabel)
    .filter(([value]) => !MandatoryPaymentTypes.includes(value as TravelOrderPaymentType))
    .map(([value, label]) => ({ id: value, name: label }));
});

const activePayments = computed<TravelOrderPayment[]>(() => {
  return payments.value.filter((payment) => payment.isActive);
});

const visiblePayments = computed<TravelOrderPayment[]>(() => {
  return isNoActivePayments.value ? payments.value : activePayments.value;
});

const rows = computed(() => {
  return visiblePayments.value.map((payment, index) => {
    const isMandatory = payment.type !== null && MandatoryPaymentTypes.includes(payment.type);

    return {
      key: getEntityKey(payment),
      payment,
      number: index + 1,
      isMandatory,
      isInactive: !payment.isActive,
      isDisabled: !payment.isActive || payment.isDeleted || isNoActivePayments.value,
      typeLabel: payment.type ? TravelOrderPaymentTypeLabel[payment.type] : "",
      typeRules: payment.isActive ? [requiredValidator] : [],
      rateRules: payment.isActive
        ? [requiredValidator, ...getPositiveRateRules(payment.type, props.travelOrderStatus)]
        : [],
    };
  });
});

const totalRate = computed<string>(() => {
  return price(
    String(
      round(
        activePayments.value.reduce(
          (total, payment) => (payment.isDeleted ? total : total + Number(payment.amount ?? 0)),
          0,
        ),
      ),
    ),
  );
});

function updatePayment(target: TravelOrderPayment, data: Partial<TravelOrderPayment>) {
  payments.value = payments.value.map((payment) => {
    return isSameEntity(payment, target) ? { ...payment, ...data } : payment;
  });
}

function addPayment() {
  payments.value = [
    ...payments.value,
    { amount: null, type: null, status: TravelOrderPaymentStatusEnum.New, isActive: true, tempId: generateId() },
  ];

  emits("scrollBottom");
}

function deletePayment(target: TravelOrderPayment) {
  if (target.id) {
    updatePayment(target, { isDeleted: true });
    return;
  }

  payments.value = payments.value.filter((payment) => !isSameEntity(payment, target));
}

function restorePayment(target: TravelOrderPayment) {
  updatePayment(target, { isDeleted: false });
}
</script>

<template>
  <div class="d-flex flex-column row-gap-3">
    <span class="trip-payments__title">{{ t("general.payments") }}</span>

    <div
      v-for="row in rows"
      :key="row.key"
      class="d-flex flex-column row-gap-3 pa-4 rounded-lg trip-payments__item"
      data-qa="payment-block"
    >
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center col-gap-4">
          <span class="trip-payments__number">{{ t("general.payment") }} #{{ row.number }}</span>

          <span v-if="row.payment.isDeleted" class="trip-payments__deleted-badge">
            {{ t("payments.deleted") }}
          </span>
          <travel-order-payment-status v-else :status="row.payment.status" />
        </div>

        <v-btn
          v-if="row.payment.isDeleted && !row.isInactive"
          class="text-uppercase font-size-11"
          color="primary"
          data-qa="payment-restore"
          size="small"
          variant="text"
          @click="restorePayment(row.payment)"
        >
          {{ t("payments.restore-payment") }}
        </v-btn>

        <v-btn
          v-else-if="!row.isMandatory && !row.isInactive"
          class="text-uppercase font-weight-600 font-size-11"
          color="danger"
          size="small"
          variant="text"
          @click="deletePayment(row.payment)"
        >
          <template #prepend>
            <v-icon icon="mdi-delete-outline" size="16" />
          </template>
          {{ t("payments.delete-payment") }}
        </v-btn>
      </div>

      <div class="d-flex align-start col-gap-4">
        <omni-select
          :disabled="row.isDisabled"
          :items="typeOptions"
          :label="t('general.type')"
          :model-value="row.payment.type"
          :readonly="row.isMandatory"
          :rules="row.typeRules"
          class="required trip-payments__field"
          data-qa="modal-payment-type"
          required
          @update:model-value="updatePayment(row.payment, { type: $event })"
        >
          <template v-if="row.payment.type" #selection>{{ row.typeLabel }}</template>
        </omni-select>

        <v-text-field
          :disabled="row.isDisabled"
          :label="t('general.rate')"
          :model-value="row.payment.amount"
          :rules="row.rateRules"
          autocomplete="off"
          class="required trip-payments__field"
          data-qa="modal-rate-field"
          density="compact"
          variant="outlined"
          @update:model-value="updatePayment(row.payment, { amount: $event })"
        >
          <template #append-inner>
            <span class="font-size-16 text-grey-darken-1">$</span>
          </template>
        </v-text-field>
      </div>
    </div>

    <div class="d-flex align-center justify-space-between pr-4">
      <tooltip-button :rules="addPaymentDisableRules">
        <template #default="{ disabled }">
          <v-btn :disabled="disabled" color="primary" size="small" variant="text" @click="addPayment">
            <template #prepend>
              <v-icon size="16">mdi-plus</v-icon>
            </template>
            {{ t("payments.add-payment") }}
          </v-btn>
        </template>
      </tooltip-button>

      <div class="d-flex align-center col-gap-3 trip-payments__total">
        <span>{{ t("general.total-rate") }}:</span>
        <span data-qa="travel-order-payments-total">{{ totalRate }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.trip-payments {
  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: $grey-navy;
  }

  &__item {
    border: 1px solid $grey-lighter;
    background-color: $grey-lighten-5;
  }

  &__number {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: $grey;
  }

  &__deleted-badge {
    padding: 4px 8px;
    border-radius: 4px;
    background-color: $dark-grey;
    font-size: 11px;
    font-weight: 500;
    line-height: 14px;
    text-transform: uppercase;
    color: $white;
  }

  &__field {
    flex: 1 1 0;
  }

  &__total {
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    color: $grey-navy;
  }
}
</style>
